<template>
  <div class="console-footer-bar" @mousedown.stop.prevent>
    <!-- Destination pill: mirrors the parsed `>` redirection target (a
         document/calendar) or a destination picked via the DestinationPicker.
         Shown as an affordance (muted placeholder when empty) whenever the
         selected type supports destinations at all (`showDestination`). A
         parser-set destination can only be changed by editing the input, so
         the pill is then read-only (no clear cross, not clickable);
         otherwise clicking it opens the picker. -->
    <button
      v-if="showDestination"
      type="button"
      class="bar-action bar-action--destination"
      :class="{
        'bar-action--empty': !destination,
        'bar-action--readonly': destination?.parserFilled,
      }"
      @mousedown.stop.prevent
      @click.stop="onDestinationClick"
      @mouseenter="onDestinationEnter"
      @mouseleave="hideTooltip"
    >
      <MoveToIcon v-if="!destination" class="bar-icon" />
      <span
        v-else-if="typeof destination.icon === 'string'"
        class="bar-emoji"
        >{{ destination.icon }}</span
      >
      <component :is="destination.icon" v-else class="bar-icon" />
      <span class="bar-label">{{
        destination?.label ?? destinationPlaceholder
      }}</span>
      <span
        v-if="destination && !destination.parserFilled"
        class="bar-clear"
        title="Clear destination"
        @mousedown.stop.prevent
        @click.stop="$emit('clear-destination')"
      >
        <CloseIcon class="bar-clear-icon" />
      </span>
    </button>
    <span v-else class="bar-spacer" />

    <div class="bar-actions">
      <!-- In capture mode (macOS with the plugin enabled) a secondary Capture
           on the left performs the capture (Shift+Enter); it is an
           UpgradeButton: without the entitlement it shows a lock and opens
           the upgrade modal instead. Create stays the primary action on the
           right (Enter / Mod+Enter). -->
      <UpgradeButton
        v-if="showCapture"
        :entitlement-key="captureEntitlementKey"
        :entitled="captureAvailability.entitled"
        respect-disabled
        kind="important"
        type="secondary"
        :state="canCapture ? 'default' : 'disabled'"
        :text="captureLabel"
        :tooltip="captureTooltip"
        tooltip-display="always"
        class="bar-add"
        @click="onCaptureClick"
      >
        <template v-if="contextIcon" #icon>
          <BaseContextIcon
            :context="contextIcon"
            class="bar-capture-context-icon"
          />
        </template>
      </UpgradeButton>
      <BaseButton
        kind="important"
        type="primary"
        :state="canAdd ? 'default' : 'disabled'"
        :text="createLabel"
        :tooltip="createTooltip"
        tooltip-display="always"
        class="bar-add"
        @click="onCreateClick"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  type Component,
} from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseContextIcon from "@/components/base/BaseContextIcon.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import {
  CONTEXTUAL_CAPTURE_ENTITLEMENT,
  type CaptureAvailability,
} from "@/components/console/capture";
import { getIcon } from "@/components/context";
import { getBuiltinKind } from "@/components/databases";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import MoveToIcon from "@/components/icons/MoveToIcon.vue";
import { useTooltip, type TooltipContent } from "@/components/tooltip";
import type { Context } from "@/types";
import { hotkeys } from "@/utils/hotkeys";
import { in_mac } from "@/utils/in";
import { keymap } from "@/utils/keymap";

/**
 * The destination pill's content: the parsed `>` redirection target or a
 * destination picked via the DestinationPicker. A parser-set destination can
 * only be changed by editing the input, so the pill is then read-only.
 */
export type PillDestination = {
  label: string;
  // A component for built-in kinds (task/page/event/person) or an emoji
  // string for a custom object.
  icon: Component | string;
  parserFilled: boolean;
};

export default defineComponent({
  name: "ConsoleFooterBar",
  components: {
    BaseButton,
    BaseContextIcon,
    CloseIcon,
    MoveToIcon,
    UpgradeButton,
  },
  props: {
    type: { type: Object as PropType<GraphDatabase>, required: true },
    // Display name overriding the database name in the Add button's label.
    typeLabel: {
      type: String as PropType<string | null>,
      default: null,
    },
    destination: {
      type: Object as PropType<PillDestination | null>,
      default: null,
    },
    // Whether the destination pill is shown at all. False when objects of the
    // selected type cannot be filed under a parent (see isTypeMovable).
    showDestination: {
      type: Boolean,
      default: true,
    },
    // Whether the input holds something submittable; the Create button is
    // disabled otherwise.
    canAdd: {
      type: Boolean,
      default: false,
    },
    // Whether the capture is submittable. Unlike `canAdd` this doesn't
    // require a typed title: an empty input captures with the context's
    // default title (selection, tab name, …).
    canCapture: {
      type: Boolean,
      default: false,
    },
    // Whether a contextual capture is possible (a context is attached and
    // the type can store it): a secondary Capture button then shows left of
    // Create. Removing the context pill hides it again.
    capture: {
      type: Boolean,
      default: false,
    },
    // The attached capture context; its icon is shown on the Capture button.
    context: {
      type: Object as PropType<Context | null>,
      default: null,
    },
    // Entitlement/plugin state for contextual capture: without the
    // entitlement the Capture button shows as a locked UpgradeButton;
    // entitled but with the plugin disabled it stays visible and the click
    // is gated downstream (plugin modal).
    captureAvailability: {
      type: Object as PropType<CaptureAvailability>,
      default: () => ({ entitled: false, enabled: false }),
    },
  },
  emits: {
    "select-destination": (_element?: HTMLElement) => true,
    "clear-destination": () => true,
    // Plain creation without storing the context (the primary Create
    // button); `isOpeningItem` is true on Mod+Click, mirroring Mod+Enter:
    // the created object is opened right away.
    add: (_isOpeningItem: boolean) => true,
    // Capture submit (the secondary Capture button, or Shift+Enter).
    capture: (_isOpeningItem: boolean) => true,
  },
  setup(props, { emit }) {
    const typeName = computed(
      () => props.typeLabel ?? (props.type.data_name || props.type.name)
    );
    const destinationPlaceholder = computed(() =>
      getBuiltinKind(props.type) === "task" ? "Inbox" : "Destination"
    );
    const createLabel = computed(
      () => `Create ${typeName.value.toLowerCase()}`
    );
    const captureLabel = computed(
      () => `Capture ${typeName.value.toLowerCase()}`
    );
    const contextIcon = computed(() => getIcon(props.context));
    // Contextual capture is macOS-only. The button shows even when it can't
    // be performed yet: without the entitlement the UpgradeButton itself
    // shows a lock and opens the upgrade modal; entitled but with the plugin
    // disabled, the click flows through the console's submit gate which
    // opens the plugin modal instead of creating anything.
    const showCapture = computed(() => props.capture && in_mac());

    const elementFrom = (event: MouseEvent): HTMLElement | undefined =>
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : undefined;

    const tooltipEl = ref<HTMLElement | undefined>(undefined);
    const tooltipContent = ref<TooltipContent>(undefined);
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(tooltipEl, tooltipContent, "top");
    onBeforeUnmount(releaseTooltip);
    const onDestinationEnter = (event: MouseEvent) => {
      const element = elementFrom(event);
      if (element) {
        tooltipEl.value = element;
        tooltipContent.value = { text: "Select a destination", shortcut: ">" };
        showTooltip();
      }
    };
    const onDestinationClick = (event: MouseEvent) => {
      hideTooltip();
      if (!props.destination?.parserFilled) {
        emit("select-destination", elementFrom(event));
      }
    };
    const isOpeningItem = (event: MouseEvent) => event.metaKey || event.ctrlKey;
    const onCaptureClick = (event: MouseEvent) =>
      emit("capture", isOpeningItem(event));
    // Plain creation: a context may still be attached (capture is only the
    // secondary action), so always submit the context-less variant.
    const onCreateClick = (event: MouseEvent) =>
      emit("add", isOpeningItem(event));

    // Advertise the submit shortcuts on the buttons, with the same key
    // symbols as the console action rows (Enter is "↵", the platform
    // primary modifier is ⌘ on macOS and ⌃ elsewhere).
    const enterKeysDisplay =
      hotkeys.format.stringify(
        keymap.keyEnter,
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    const modEnterKeysDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyEnter],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    const shiftEnterKeysDisplay =
      hotkeys.format.stringify(
        [keymap.keyShift, keymap.keyEnter],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    const createTooltip = computed<TooltipContent>(() => [
      { text: "Create", shortcut: enterKeysDisplay },
      { text: "Create & open", shortcut: modEnterKeysDisplay },
    ]);
    const modShiftEnterKeysDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyEnter],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";
    const captureTooltip = computed<TooltipContent>(() => [
      { text: "Capture", shortcut: shiftEnterKeysDisplay },
      { text: "Capture & open", shortcut: modShiftEnterKeysDisplay },
    ]);

    return {
      captureEntitlementKey: CONTEXTUAL_CAPTURE_ENTITLEMENT,
      captureLabel,
      captureTooltip,
      contextIcon,
      createLabel,
      createTooltip,
      destinationPlaceholder,
      hideTooltip,
      onCaptureClick,
      onCreateClick,
      onDestinationClick,
      onDestinationEnter,
      showCapture,
    };
  },
});
</script>

<style scoped lang="scss">
// The footer bar, separated from the content above by a full-width hairline:
// the destination selector on the left, the Add action on the right.
.console-footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 10px 0px 10px;
  @include themed() {
    border-top: 1px solid t($grey-10);
  }
}

.bar-spacer {
  flex: 1;
}

.bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.bar-add {
  flex-shrink: 0;

  .bar-capture-context-icon {
    width: 14px;
    height: 14px;
  }
}

.bar-action {
  @extend %font-caption-regular;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;
  padding: 3px 8px;
  border: 1px solid transparent;
  border-radius: $border-radius-m;
  cursor: default;
  @include themed() {
    background-color: transparent;
    color: t($grey-70);
  }
  @include transition(background-color 0.1s ease-out, color 0.1s ease-out);

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }
}

.bar-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.bar-action--destination {
  .bar-icon :deep(.fill) {
    @include themed() {
      fill: t($grey-60);
    }
  }

  .bar-icon :deep(.stroke) {
    @include themed() {
      stroke: t($grey-60);
    }
  }
}

.bar-action--empty {
  @include themed() {
    color: t($grey-70);
  }

  .bar-icon :deep(.fill) {
    @include themed() {
      fill: t($grey-50);
    }
  }

  .bar-icon :deep(.stroke) {
    @include themed() {
      stroke: t($grey-50);
    }
  }
}

.bar-action--readonly:hover {
  @include themed() {
    background-color: transparent;
  }
}

.bar-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;

  :deep(svg) {
    display: block;
    width: 13px;
    height: 13px;
  }
}

.bar-emoji {
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
}

.bar-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-inline-start: -1px;
  margin-inline-end: -4px;
  border-radius: 50%;
  opacity: 0.55;
  flex-shrink: 0;
  @include transition(opacity 0.1s ease-out, background-color 0.1s ease-out);

  &:hover {
    opacity: 1;
    @include themed() {
      background-color: t($grey-10);
    }
  }
}

.bar-clear-icon {
  width: 11px;
  height: 11px;

  :deep(.fill) {
    @include themed() {
      fill: currentColor;
    }
  }
}
</style>
